import { useAppDispatch, useAppSelector } from "../../../hooks";
import {
  getDBSMetricValuesAsync,
  selectDBSMetrics,
  selectDBSMetricsLoading,
  selectDatabaseService,
} from "../../../store/database/serviceSlice";
import { useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  // Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DBSCategory, IDBSMetricData } from "../../../types/database";
import { formatTimestamp } from "../../../utils/date";
import { GraphChartCustomTooltipProps } from "../../../types/s3";
import { round } from "../../../utils";
import { generateServicePermissions } from "../../../pages/databases/ServicePage";
import { DataServiceGeneralInformationUrl } from "../../../utils/urls";
import { useNavigate } from "react-router-dom";
import {
  Flex,
  Loading,
  Select,
  SelectOption,
  Switcher,
  Typography,
} from "djuno-design";
import { EmptyLineChart } from "../../layouts/NotData";

const periods: Array<SelectOption> = [
  { label: "1 hour", value: "lastHour" },
  { label: "1 day", value: "lastDay" },
  { label: "1 week", value: "lastWeek" },
  { label: "1 month", value: "lastMonth" },
  { label: "1 year", value: "lastYear" },
];

const DatabaseMetricsTab: React.FC<{ category: DBSCategory }> = ({
  category,
}) => {
  const service = useAppSelector(selectDatabaseService);

  const metrics = useAppSelector(selectDBSMetrics);
  const metricsLoading = useAppSelector(selectDBSMetricsLoading);

  const [autoRefresh, setAutoRefresh] = useState<boolean>(false);
  const [selectedPeriod, setSelectedPeriod] = useState(periods[0].value);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (service) {
      const permissions = generateServicePermissions().metrics;
      if (!permissions.includes(service.engine)) {
        navigate(DataServiceGeneralInformationUrl(service.engine, service.id));
      } else {
        dispatch(
          getDBSMetricValuesAsync({
            engine: service.engine,
            clusterId: service.id,
            period: selectedPeriod,
            withoutError: true,
          })
        );
      }
    }
  }, [dispatch, navigate, category, selectedPeriod, service]);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    if (service && autoRefresh) {
      interval = setInterval(() => {
        dispatch(
          getDBSMetricValuesAsync({
            engine: service.engine,
            clusterId: service.id,
            withoutLoading: true,
            withoutError: true,
            period: selectedPeriod,
          })
        );
      }, 60000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [dispatch, service, autoRefresh, selectedPeriod]);

  return (
    <div>
      <div className="flex flex-col items-start w-full justify-between gap-2 pb-10">
        <Typography.Text className="!text-sm">
          To help you track and manage your database instance, view its main
          metrics and statistics below. Their retention period depends on your
          service solution.
        </Typography.Text>
        <div className="flex items-center justify-between w-full mb-5">
          <Select
            className="w-44"
            emptyString="Select an option"
            options={periods}
            value={selectedPeriod}
            onChange={(v) => setSelectedPeriod(v || periods[0].value)}
          />
          <div className="flex items-center gap-0.5">
            <Typography.Text className="!text-xs">
              Auto-Refresh (1 min.)
            </Typography.Text>
            <Switcher value={autoRefresh} onChange={setAutoRefresh} />
          </div>
        </div>
        {metricsLoading && (
          <Flex
            items="center"
            justify="center"
            className="!min-h-[300px] w-full"
          >
            <Loading borderSize={2} />
          </Flex>
        )}
        {!metricsLoading && Object.keys(metrics).length > 0 && (
          <div className="flex flex-col w-full gap-14">
            {Object.keys(metrics).map((key, i) => {
              return (
                <div key={i} className="h-[200px]">
                  <div className="w-full flex justify-center mt-5">
                    <Typography.Text className="!font-semibold !text-xs">
                      {chartName(metrics[key].name)}
                    </Typography.Text>
                  </div>
                  <DBSMetricChart
                    data={metrics[key]}
                    dataDivider={dataDivider(metrics[key].name)}
                    yName={chartYName(metrics[key].name)}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const DBSMetricChart: React.FC<{
  data: IDBSMetricData;
  dataDivider?: number;
  yName?: string;
}> = ({ data, yName, dataDivider }) => {
  const combineLineDataMemo = useMemo(() => {
    return () => combineGraphLineData(data, dataDivider || 1);
  }, [data, dataDivider]);

  const renderXAxis = () => {
    return (
      <XAxis
        dataKey="name"
        tick={{
          fontSize: 10,
          className: "!text-slate-800 dark:!text-slate-300",
        }}
        className=""
        // interval={Math.ceil(combineLineDataMemo().length / 2)}
        // domain={[-1, 1]}
      />
    );
  };

  const renderYAxis = () => (
    <YAxis
      tick={{ fontSize: 10 }}
      label={{
        value: yName,
        style: { textAnchor: "middle", fontSize: 12, fontWeight: 600 },
        angle: -90,
        position: "left",
        offset: -10,
      }}
      // tickFormatter={(value) =>
      //   humanizeSize(value, { fractionDigits: 1 }).join("B")
      // }
    />
  );

  const renderChart = () => (
    <AreaChart
      data={combineLineDataMemo()}
      margin={{ top: 2, right: 10, bottom: 0, left: 10 }}
    >
      <defs>
        {data &&
          data.metrics?.map((metric, i) => {
            const _key = i * (metric.dataPoints?.length || 0);
            return (
              <linearGradient
                key={_key}
                id={`area-color-${_key}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="5%"
                  stopColor="rgba(31,143,255,0.1)"
                  stopOpacity="rgba(31,143,255,0.1)"
                />
                <stop offset="95%" stopColor="#eee" stopOpacity={0} />
              </linearGradient>
            );
          })}
      </defs>
      <CartesianGrid strokeDasharray="3 3" />
      {renderXAxis()}
      {renderYAxis()}
      <Tooltip content={GraphCustomTooltip} />
      {/* <Legend verticalAlign="bottom" hanging={200} /> */}
      {data &&
        data.metrics?.map((metric, i) => {
          const _key = i * (metric.dataPoints?.length || 0);
          return (
            <Area
              key={`area-${_key}`}
              dataKey={metric.hostname}
              type="monotone"
              fill={`url(#area-color-${_key})`}
              strokeWidth={2}
              stroke={chartColor(i)}
              strokeOpacity={1}
            />
          );
        })}
    </AreaChart>
  );

  if (data && data.metrics.length === 0)
    return <EmptyLineChart className="bg-primary-50/50 dark:bg-dark-3/80" />;
  return <ResponsiveContainer>{renderChart()}</ResponsiveContainer>;
};

export const combineGraphLineData = (
  data: IDBSMetricData | null,
  dataDivider: number,
  dateFormat: string = "DD/MM/YYYY HH:mm"
) => {
  let combinedData: Record<string, any>[] = [];
  if (
    data !== null &&
    data !== undefined &&
    data.metrics &&
    data.metrics.length > 0
  ) {
    const basicMetric = data.metrics[0];
    basicMetric.dataPoints.forEach(({ timestamp, value }) => {
      const time = formatTimestamp(timestamp, dateFormat, {
        isUTC: false,
      }).datetime;

      const entry = {
        name: time,
        timestamp,
        [basicMetric.hostname]: value / dataDivider,
      };
      combinedData.push(entry);
    });

    if (data.metrics.length > 1) {
      const extraMetrics = data.metrics.slice(1);
      for (let i = 0; i < extraMetrics.length; i++) {
        const metric = extraMetrics[i];
        combinedData = combinedData.map((cd, j) => ({
          ...cd,
          [metric.hostname]: metric.dataPoints[j]
            ? metric.dataPoints[j].value / dataDivider
            : null,
        }));
      }
    }
  }
  // Iterate over the values array of each line data
  return combinedData;
};

const GraphCustomTooltip = ({
  active,
  payload,
}: GraphChartCustomTooltipProps) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/70 dark:bg-dark-3/80 p-2 border dark:border-dark-2 rounded-md max-h-40 overflow-y-auto">
        <div className="flex flex-col">
          <Typography.Text className="!text-center !text-xs" uiType="secondary">
            {
              formatTimestamp(
                payload[0].payload.timestamp,
                "DD/MM/YYYY HH:mm",
                { isUTC: false }
              ).datetime
            }
          </Typography.Text>
          {payload.map((item, i) => (
            <div className="flex items-center gap-1" key={i}>
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: chartColor(i) }}
              />
              <Typography.Text className="!text-xs label">{`${
                item.dataKey
              } : ${round(item.payload[item.dataKey], 4)}`}</Typography.Text>
            </div>
          ))}
        </div>
      </div>
    );
  }
};

const chartName = (label: string) => {
  switch (label) {
    case "cpu_usage_percent":
      return "CPU used";

    case "disk_usage_percent":
      return "Disk space used";

    case "diskio_read":
      return "No. of IOPS";

    case "diskio_writes":
      return "No. of IOPS";

    case "mem_usage_percent":
      return "memory used";

    case "net_receive":
      return "Network Reception";

    case "net_send":
      return "Network Transmission";

    default:
      return "";
  }
};

const chartYName = (label: string) => {
  switch (label) {
    case "cpu_usage_percent":
      return "%";

    case "disk_usage_percent":
      return "%";

    case "diskio_read":
      return "read";

    case "diskio_writes":
      return "write";

    case "mem_usage_percent":
      return "%";

    case "net_receive":
      return "bytes/s";

    case "net_send":
      return "bytes/s";

    default:
      return "";
  }
};

const dataDivider = (label: string) => {
  switch (label) {
    case "cpu_usage_percent":
      return 1;

    case "disk_usage_percent":
      return 1;

    case "diskio_read":
      return 1;

    case "diskio_writes":
      return 1;

    case "mem_usage_percent":
      return 1;

    case "net_receive":
      return 1000;

    case "net_send":
      return 1000;

    default:
      return 1;
  }
};

const chartColor = (i: number) => {
  const colors = ["rgb(0 ,116 ,229)", "rgb(249 ,115 ,22)", "rgb(126 ,34 ,206)"];
  const colorsLen = colors.length;

  if (i <= colorsLen - 1) {
    return colors[i];
  } else {
    return colors[i - colorsLen];
  }
};
export default DatabaseMetricsTab;
